"use client";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClientPickerWithManualEntry from "components/ClientPickerWithManualEntry";
import InputField from "components/Input/InputField";
import UploadFile from "components/Input/UploadFile";
import SubtaskManager from "components/Tasks/SubtaskManager";
import NotifyAdminDialog from "components/Tasks/NotifyAdminDialog";
import { modules } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTasks, useCreateTask, useUpdateTask } from "utils/taskService";
import { useClientById } from "utils/clientService";
import {
  formatTaskDate,
  getTaskAdminRecipients,
  getTaskAssignableUsers,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
} from "utils/taskUtils";
import { getCheckListLabel, getPriceListLabel } from "utils/catalogService";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import DuplicateDataAlertModal from "./DuplicateDataAlertModal";
import { isModulePaymentTaskType } from "../Accounts/modulePaymentTaskTypes";
import ClientDropdown from "components/Dropdowns/ClientDropdown";

const priorityOptions = TASK_PRIORITY_OPTIONS;
const statusOptions = TASK_STATUS_OPTIONS;

const EMPTY_MANUAL_CLIENT = { name: "", email: "", phone: "" };

/**
 * Referral partners are ordinary assignees — the task tiers key off the seat on the task, not the
 * role — but they are outside the office, so the picker says so before the work is handed over.
 */
const renderAssigneeOption = (props, option) => (
  <li {...props} key={option?.id}>
    <span className="flex items-center gap-2">
      <span>{option?.name || option?.email}</span>
      {option?.type === "REFERRAL_USER" && (
        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
          Referral partner
        </span>
      )}
    </span>
  </li>
);

const FlowCard = ({
  step,
  title,
  hint,
  children,
  locked = false,
  lockHint,
  complete = false,
  active = false,
  cardRef,
}) => (
  <section
    ref={cardRef}
    className={`relative rounded-xl border p-4 md:p-5 transition-all duration-200 scroll-mt-4 ${
      locked
        ? "border-app-border bg-app-surface-muted/40 opacity-50"
        : active
        ? "border-primary/40 bg-primary/[0.03] shadow-sm ring-1 ring-primary/20"
        : complete
        ? "border-emerald-200/80 bg-app-surface"
        : "border-app-border bg-app-surface"
    }`}
  >
    <div className="flex items-start gap-3 mb-4">
      <span
        className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
          complete
            ? "bg-emerald-500 text-white"
            : active
            ? "bg-primary text-white"
            : locked
            ? "bg-app-surface-muted text-app-text-muted"
            : "bg-app-surface-muted text-app-text-secondary"
        }`}
      >
        {complete ? "✓" : step}
      </span>
      <div className="min-w-0">
        <h4 className="text-sm font-semibold text-app-text m-0">{title}</h4>
        {hint ? <p className="text-xs text-app-text-muted mt-0.5 mb-0">{hint}</p> : null}
      </div>
    </div>
    <div className={locked ? "pointer-events-none select-none" : ""}>{children}</div>
    {locked && (
      <div className="absolute inset-0 flex items-end justify-center rounded-xl bg-gradient-to-t from-app-surface/90 via-app-surface/40 to-transparent p-4">
        <p className="text-xs font-medium text-app-text-secondary bg-app-surface border border-app-border rounded-full px-3 py-1.5 shadow-sm">
          {lockHint || "Complete the previous step first"}
        </p>
      </div>
    )}
  </section>
);

const ProgressStep = ({ step, label, done, active, locked, detail }) => (
  <li className="flex gap-3">
    <div className="flex flex-col items-center">
      <span
        className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
          done
            ? "bg-emerald-500 text-white"
            : active
            ? "bg-primary text-white"
            : "bg-app-surface-muted text-app-text-muted"
        }`}
      >
        {done ? "✓" : step}
      </span>
      <span className={`w-px flex-1 min-h-[16px] ${done ? "bg-emerald-300" : "bg-app-border"}`} />
    </div>
    <div className={`pb-4 min-w-0 ${locked ? "opacity-50" : ""}`}>
      <p
        className={`text-sm m-0 ${
          active
            ? "font-semibold text-primary"
            : done
            ? "font-medium text-app-text"
            : "text-app-text-secondary"
        }`}
      >
        {label}
      </p>
      {detail ? <p className="text-xs text-app-text-muted mt-0.5 mb-0">{detail}</p> : null}
    </div>
  </li>
);

const AddTasks = ({
  color = "light",
  modal,
  modalData,
  module,
  handleClose,
  isEditing,
  editingData,
  refetchTaskDetails,
  taskType,
  admissionId,
  moduleEntryType,
  moduleEntryId,
  termIndex,
  termDataSnapshot,
  paymentUuid,
}) => {
  const initailState = {
    clientId: null,
    module: null,
    specificTask: "",
    assignedToId: null,
    deadline: null,
    comment: null,
    attachments: [],
    priority: null,
    status: null,
    priceList: null,
    checkList: null,
  };
  const navigate = useNavigate();
  const [
    { usersList, priceList, checkList },
    { refetchUsers, refetchPriceList, refetchCheckList },
  ] = useKothar();
  useEffect(() => {
    if (!usersList?.length) {
      refetchUsers();
    }
  }, []);
  const [data, setData] = useState(initailState);
  const [useManualClient, setUseManualClient] = useState(false);
  const [manualClient, setManualClient] = useState({ ...EMPTY_MANUAL_CLIENT });
  const [additionalAssignees, setAdditionalAssignees] = useState([]);
  const [draftSubtasks, setDraftSubtasks] = useState([]);
  const [notifyAdmin, setNotifyAdmin] = useState(false);
  const [notifyAdminMessage, setNotifyAdminMessage] = useState("");
  const [showNotifyAdminDialog, setShowNotifyAdminDialog] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [pendingPeopleChange, setPendingPeopleChange] = useState(null);
  const [optionalExpanded, setOptionalExpanded] = useState({
    subtasks: false,
    attachments: false,
  });
  const essentialsRef = useRef(null);
  const peopleRef = useRef(null);

  const userData = JSON.parse(localStorage.getItem("userDetail"));
  const orgUsers = useMemo(
    () => usersList?.filter((user) => user?.organization?.id === userData?.orgId),
    [usersList, userData?.orgId]
  );
  const { refetch, data: tasksList } = useTasks();
  const [openAlertModal, setOpenAlertModal] = useState({ state: false, message: "" });
  const { data: duplicateAlertClient } = useClientById(
    openAlertModal?.state ? openAlertModal?.data : null
  );

  const lockedClient = modal && Boolean(module === "Client" ? modalData?.id : modalData?.clientId);
  const lockedModulePaymentTask = modal && isModulePaymentTaskType(taskType);

  const selectedModuleValue =
    (typeof data?.module === "object" ? data?.module?.value : data?.module) || null;
  const selectedModuleLabel =
    (typeof data?.module === "object" ? data?.module?.label : null) ||
    modules?.find((m) => m.value === selectedModuleValue)?.label ||
    selectedModuleValue;

  useEffect(() => {
    if (!selectedModuleValue) return;
    if (!priceList?.length) refetchPriceList();
    if (!checkList?.length) refetchCheckList();
  }, [
    selectedModuleValue,
    priceList?.length,
    checkList?.length,
    refetchPriceList,
    refetchCheckList,
  ]);

  const modulePriceListOptions = useMemo(() => {
    const raw = Array.isArray(priceList) ? priceList : priceList?.content || [];
    return raw;
  }, [priceList, selectedModuleValue, selectedModuleLabel]);

  const checkListOptions = useMemo(() => {
    if (Array.isArray(checkList)) return checkList;
    return checkList?.content || [];
  }, [checkList]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevState) => ({ ...prevState, [name]: value }));
  };

  useEffect(() => {
    if (modal && modalData) {
      setData((prevData) => ({
        ...prevData,
        clientId: {
          name: modalData?.name,
          id: module === "Client" ? modalData?.id : modalData?.clientId,
        },
        module: modules?.find((id) => id.value === module),
        specificTask: modalData?.specificTask ?? prevData.specificTask ?? "",
        deadline: modalData?.deadline ?? prevData.deadline ?? null,
        comment: modalData?.comment ?? prevData.comment ?? null,
        priceList: null,
        checkList: null,
      }));
      setUseManualClient(false);
      setManualClient({ ...EMPTY_MANUAL_CLIENT });
    }
  }, [modal, modalData, module]);

  useEffect(() => {
    if (!isEditing || !editingData) return;
    const next = { ...editingData };
    next.name = editingData?.clientName ?? editingData?.name ?? "";
    setData(next);
    setDraftSubtasks(Array.isArray(editingData?.subtasks) ? editingData.subtasks : []);
    const hasSubtasks = Array.isArray(editingData?.subtasks) && editingData.subtasks.length > 0;
    const hasAttachments =
      Array.isArray(editingData?.attachments) && editingData.attachments.length > 0;
    setOptionalExpanded((prev) => ({
      ...prev,
      subtasks: hasSubtasks || prev.subtasks,
      attachments: hasAttachments || prev.attachments,
    }));
  }, [isEditing, editingData]);

  useEffect(() => {
    if (!isEditing) return;
    setData((prev) => {
      if (!prev?.priceListId && !prev?.checkListId) return prev;
      const nextPriceList =
        prev.priceList ??
        (prev.priceListId
          ? modulePriceListOptions.find((p) => p.id === prev.priceListId) || null
          : null);
      const nextCheckList =
        prev.checkList ??
        (prev.checkListId ? checkListOptions.find((c) => c.id === prev.checkListId) || null : null);
      if (nextPriceList === prev.priceList && nextCheckList === prev.checkList) return prev;
      return { ...prev, priceList: nextPriceList, checkList: nextCheckList };
    });
  }, [isEditing, modulePriceListOptions, checkListOptions]);

  useEffect(() => {
    if (!isEditing || !editingData) return;
    const extraIds = editingData?.additionalAssigneeIds || [];
    setAdditionalAssignees(orgUsers?.filter((u) => extraIds.includes(u.id)) || []);
    if (orgUsers?.length) {
    }
  }, [isEditing, editingData, orgUsers]);

  const { mutate: createTask, isLoading: isCreatingTask } = useCreateTask({
    onSuccess: (response) => {
      toast.success("Task added Successfully");
      if (modal) {
        handleClose();
      } else {
        navigate("/admin/tasks");
        refetch();
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.errorMessage || "Error Submitting Task");
    },
  });

  const { mutate: updateTask, isLoading: isUpdatingTask } = useUpdateTask({
    onSuccess: (response) => {
      toast.success("Task updated Successfully");
      refetchTaskDetails(response?.data?.data?.id);
      refetch();
      if (modal) {
        handleClose();
      } else {
        navigate("/admin/tasks");
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.errorMessage || "Error Updating Task");
    },
  });

  const scrollTo = (ref) => {
    requestAnimationFrame(() => {
      ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    if (!isDetailsComplete) {
      toast.error("Please fill in all required details");
      scrollTo(essentialsRef);
      return;
    }
    if (!(data?.assignTo?.id || data?.reassignedTo?.id || data?.assignedToId)) {
      toast.error("Please mention who you are assigning this task to ");
      scrollTo(peopleRef);
      return;
    }
    if (notifyAdmin && !notifyAdminMessage.trim()) {
      toast.error("Write a message for the admins, or uncheck the notify option");
      scrollTo(peopleRef);
      setShowNotifyAdminDialog(true);
      return;
    }

    const additionalAssigneeIds = additionalAssignees.map((u) => u.id);
    const subtasksPayload = draftSubtasks
      .filter((s) => s?.title?.trim())
      .map((s) => ({
        id: s.id ?? null,
        title: s.title.trim(),
        assigneeId: s.assigneeId ?? null,
        deadline: s.deadline ?? null,
        completed: Boolean(s.completed),
      }));
    const sharedExtras = {
      additionalAssigneeIds,
      subtasks: subtasksPayload,
      notifyAdmin,
      notifyAdminMessage: notifyAdmin ? notifyAdminMessage : null,
      priceListId: data?.priceList?.id ?? data?.priceListId ?? null,
      checkListId: data?.checkList?.id ?? data?.checkListId ?? null,
    };

    if (isEditing && data?.id) {
      updateTask({
        id: data?.id,
        taskData: {
          clientId: data?.clientId,
          module: data?.module,
          specificTask: data?.specificTask,
          deadline: data?.deadline,
          comment: data?.comment,
          attachments: data?.attachments,
          priority: data?.priority?.value ?? data?.priority,
          status: data?.status?.value ?? data?.status,
          reasonOfReassignment: data?.reasonOfReassignment,
          assignTo: data?.reassignedTo?.id ?? (data?.assignTo?.id || data?.assignedToId),
          ...sharedExtras,
        },
      });
    } else {
      const taskPayload = {
        specificTask: data?.specificTask,
        deadline: data?.deadline,
        comment: data?.comment,
        attachments: data?.attachments,
        assignTo: data?.assignTo?.id,
        module: data?.module?.value,
        priority: data?.priority?.value,
        taskType: taskType ?? null,
        admissionId: admissionId ?? null,
        moduleEntryType: moduleEntryType ?? null,
        moduleEntryId: moduleEntryId ?? null,
        termIndex: termIndex ?? null,
        termDataSnapshot: termDataSnapshot ?? null,
        paymentUuid: paymentUuid ?? null,
        ...sharedExtras,
      };

      if (lockedClient) {
        const resolvedClientId =
          module === "Client" ? modalData?.id : modalData?.clientId ?? data?.clientId?.id;
        createTask({
          ...taskPayload,
          existingClient: true,
          clientId: resolvedClientId,
        });
        return;
      }

      if (!useManualClient) {
        if (!data?.clientId?.id) {
          toast.error("Please select a client");
          scrollTo(essentialsRef);
          return;
        }
        createTask({
          ...taskPayload,
          existingClient: true,
          clientId: data.clientId.id,
        });
        return;
      }

      const clientName = manualClient.name.trim();
      const clientEmail = manualClient.email.trim();
      const clientPhoneNumber = manualClient.phone?.trim() || null;
      if (!clientName || !clientEmail) {
        toast.error("Client name and email are required");
        scrollTo(essentialsRef);
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail)) {
        toast.error("Enter a valid client email");
        scrollTo(essentialsRef);
        return;
      }

      createTask({
        ...taskPayload,
        existingClient: false,
        clientName,
        clientEmail,
        clientPhoneNumber,
      });
    }
  };

  const cancelSelectedAssignees = orgUsers?.filter((user) => user?.email !== userData?.email) || [];
  const primaryAssigneeId = data?.assignTo?.id || data?.assignedToId;
  const additionalOptions = cancelSelectedAssignees.filter((u) => u.id !== primaryAssigneeId);

  const adminRecipients = getTaskAdminRecipients({
    participants: [
      data?.assignTo?.id ? data.assignTo : orgUsers?.find((u) => u.id === primaryAssigneeId),
      ...additionalAssignees,
    ].filter(Boolean),
    directory: orgUsers || [],
    excludeId: userData?.id,
    excludeEmail: userData?.email,
  });
  const canNotifyAdmin = adminRecipients.length > 0;

  const currentUser = orgUsers?.find((user) => user?.email === userData?.email) || null;
  const primaryAssignee =
    (data?.assignTo?.id ? data.assignTo : orgUsers?.find((u) => u.id === primaryAssigneeId)) ||
    null;
  const subtaskAssignableUsers = getTaskAssignableUsers({
    people: [currentUser, primaryAssignee, ...additionalAssignees].filter(Boolean),
    directory: orgUsers || [],
  });

  /**
   * People changes are gated because a draft subtask may be held by whoever is being dropped.
   * The backend rejects a subtask assigned to a non-participant, so the assignment has to be
   * cleared here — but never silently.
   */
  const requestPeopleChange = (apply, nextPeopleIds) => {
    const orphaned = draftSubtasks.filter(
      (subtask) => subtask?.assigneeId != null && !nextPeopleIds.has(subtask.assigneeId)
    );
    if (!orphaned.length) {
      apply();
      return;
    }
    setPendingPeopleChange({ apply, orphanedIds: orphaned.map((subtask) => subtask.assigneeId) });
  };

  const confirmPeopleChange = () => {
    const dropped = new Set(pendingPeopleChange.orphanedIds);
    setDraftSubtasks((previous) =>
      previous.map((subtask) =>
        subtask?.assigneeId != null && dropped.has(subtask.assigneeId)
          ? { ...subtask, assigneeId: null }
          : subtask
      )
    );
    pendingPeopleChange.apply();
    setPendingPeopleChange(null);
  };

  const orphanedSubtaskCount = pendingPeopleChange?.orphanedIds?.length ?? 0;

  useEffect(() => {
    if (!canNotifyAdmin && notifyAdmin) {
      setNotifyAdmin(false);
      setNotifyAdminMessage("");
    }
  }, [canNotifyAdmin, notifyAdmin]);

  const hasClientSelected =
    lockedClient ||
    (useManualClient
      ? Boolean(manualClient.name && manualClient.email)
      : Boolean(isEditing ? data?.clientId : data?.clientId?.id));
  const isDetailsComplete = Boolean(
    hasClientSelected && data?.module && data?.specificTask?.trim() && data?.priority
  );
  const isPeopleComplete = Boolean(primaryAssigneeId);
  const assignUnlocked = isDetailsComplete || isEditing;
  const extrasUnlocked = isPeopleComplete || isEditing;
  const currentStep = !isDetailsComplete ? 1 : !isPeopleComplete ? 2 : 3;
  const subtasksCount = draftSubtasks.filter((s) => s?.title?.trim()).length;
  const attachmentsCount = Array.isArray(data?.attachments) ? data.attachments.length : 0;

  const toggleOptional = (key) => setOptionalExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedClientName =
    (lockedClient ? modalData?.name : null) ||
    (useManualClient ? manualClient?.name : null) ||
    (isEditing ? data?.name || data?.clientName : data?.clientId?.name) ||
    null;
  const selectedPriorityLabel =
    priorityOptions?.find(
      (option) =>
        option?.value ===
        (typeof data?.priority === "object" ? data?.priority?.value : data?.priority)
    )?.label || null;
  const summaryItems = [
    { label: "Client", value: selectedClientName },
    { label: "Module", value: selectedModuleLabel },
    { label: "Price list", value: data?.priceList ? getPriceListLabel(data.priceList) : null },
    { label: "Check list", value: data?.checkList ? getCheckListLabel(data.checkList) : null },
    { label: "Deadline", value: data?.deadline ? formatTaskDate(data.deadline) : null },
    { label: "Priority", value: selectedPriorityLabel },
  ].filter((item) => Boolean(item.value));

  const missingEssentials = [
    !hasClientSelected && "client",
    !data?.specificTask?.trim() && "title",
    !selectedModuleValue && "module",
    !data?.priority && "priority",
  ].filter(Boolean);

  return (
    <div className={!modal ? "flex flex-wrap mt-4 dashBody" : "flex flex-wrap mt-4"}>
      <div className="w-full px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-6 md:px-8 py-4 border-b border-app-border">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {!modal && (
                <div className="relative w-full max-w-full flex justify-start gap-4 items-center">
                  <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
                  <div>
                    <h3 className="font-semibold text-xl text-app-text m-0">
                      {data?.id ? "Edit task" : "New task"}
                    </h3>
                    <p className="text-xs text-app-text-muted mt-0.5 mb-0">
                      Follow the steps on the left — the guide on the right shows what’s next.
                    </p>
                  </div>
                </div>
              )}
              {modal && (
                <p className="text-xs text-app-text-muted m-0">
                  Fill the essentials, assign an owner, then save.
                </p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 md:px-8 py-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                {/* Flow */}
                <div
                  className={`lg:col-span-8 space-y-5 min-w-0 ${
                    modal
                      ? ""
                      : "lg:max-h-[calc(100vh-14rem)] lg:overflow-y-auto lg:pr-3 lg:-mr-3 lg:pb-2"
                  }`}
                >
                  <FlowCard
                    cardRef={essentialsRef}
                    step={1}
                    title="Essentials"
                    hint="Client, title, module and priority. Price list and check list appear once a module is picked."
                    complete={isDetailsComplete}
                    active={currentStep === 1}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="relative w-full md:col-span-2">
                        {isEditing ? (
                          <ClientDropdown
                            data={data ?? null}
                            setData={setData}
                            disabled={lockedClient}
                            hideEditButton
                          />
                        ) : (
                          <ClientPickerWithManualEntry
                            existingValue={data?.clientId ?? null}
                            onExistingChange={(value) =>
                              setData((prev) => ({ ...prev, clientId: value }))
                            }
                            manualClient={manualClient}
                            onManualChange={setManualClient}
                            useManual={useManualClient}
                            onUseManualChange={setUseManualClient}
                            locked={lockedClient}
                            lockedDisplayName={modalData?.name}
                            autocompleteLabel="Select client"
                            onExistingSelectAttempt={(value) => {
                              if (!value) return true;
                              const dataExistsForClient = tasksList?.some(
                                (task) =>
                                  task?.clientId === value?.id &&
                                  task?.status !== "COMPLETED" &&
                                  task?.status !== "CANCELLED"
                              );
                              if (dataExistsForClient) {
                                setOpenAlertModal({
                                  state: true,
                                  message:
                                    "A task already exists for this client. Are you sure you want to proceed?",
                                  data: value?.id,
                                });
                                return false;
                              }
                              return true;
                            }}
                          />
                        )}
                        {attemptedSubmit && !hasClientSelected && (
                          <p className="text-xs text-red-500 mt-1">Please select or add a client</p>
                        )}
                      </div>

                      <div className="relative w-full md:col-span-2">
                        <InputField
                          label="Task Title"
                          placeholder="What needs to be done?"
                          name="specificTask"
                          required
                          multiline
                          rows={2}
                          value={data?.specificTask}
                          onChange={handleInputChange}
                          disabled={lockedModulePaymentTask}
                          error={attemptedSubmit && !data?.specificTask?.trim()}
                          helperText={
                            attemptedSubmit && !data?.specificTask?.trim()
                              ? "Task title is required"
                              : undefined
                          }
                        />
                      </div>

                      <div className="relative w-full">
                        <label className="input-label">Module *</label>
                        <Autocomplete
                          onChange={(e, value) => {
                            setData((prevState) => ({
                              ...prevState,
                              module: value,
                              priceList: null,
                              priceListId: null,
                              checkList: null,
                              checkListId: null,
                            }));
                          }}
                          required
                          value={
                            isEditing
                              ? modules?.find((m) => m?.value === data?.module) ?? null
                              : data?.module ?? null
                          }
                          options={modules || []}
                          isOptionEqualToValue={(options, value) => options?.value === value?.value}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Module"
                              type="search"
                              inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
                              error={attemptedSubmit && !selectedModuleValue}
                              helperText={
                                attemptedSubmit && !selectedModuleValue
                                  ? "Module is required"
                                  : undefined
                              }
                            />
                          )}
                          ListboxProps={{ style: { maxHeight: "180px" } }}
                          size="small"
                          disabled={Boolean(modal && module && module !== "Client")}
                        />
                      </div>

                      <div className="relative w-full">
                        <label className="input-label">Priority *</label>
                        <FormControl fullWidth>
                          <Autocomplete
                            onChange={(e, value) => {
                              setData((prevState) => ({
                                ...prevState,
                                priority: isEditing ? value?.value : value,
                              }));
                            }}
                            value={
                              isEditing
                                ? priorityOptions?.find(
                                    (option) => option?.value === data?.priority
                                  ) ?? null
                                : data?.priority ?? null
                            }
                            options={priorityOptions || []}
                            getOptionLabel={(option) => option?.label || ""}
                            isOptionEqualToValue={(options, value) =>
                              options?.value === value?.value
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Priority"
                                type="search"
                                inputProps={{
                                  ...params.inputProps,
                                  autoComplete: "new-password",
                                }}
                                error={attemptedSubmit && !data?.priority}
                                helperText={
                                  attemptedSubmit && !data?.priority
                                    ? "Priority is required"
                                    : undefined
                                }
                              />
                            )}
                            ListboxProps={{ style: { maxHeight: "180px" } }}
                            size="small"
                          />
                        </FormControl>
                      </div>

                      {selectedModuleValue && (
                        <>
                          <div className="relative w-full">
                            <label className="input-label">Price list</label>
                            <Autocomplete
                              options={modulePriceListOptions}
                              value={data?.priceList || null}
                              onChange={(_e, value) =>
                                setData((prev) => ({
                                  ...prev,
                                  priceList: value,
                                  priceListId: value?.id ?? null,
                                }))
                              }
                              getOptionLabel={getPriceListLabel}
                              isOptionEqualToValue={(option, value) => option?.id === value?.id}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={`Price list for ${selectedModuleLabel}`}
                                  placeholder="Optional"
                                  size="small"
                                />
                              )}
                              ListboxProps={{ style: { maxHeight: "220px" } }}
                              size="small"
                            />
                          </div>
                          <div className="relative w-full">
                            <label className="input-label">Check list</label>
                            <Autocomplete
                              options={checkListOptions}
                              value={data?.checkList || null}
                              onChange={(_e, value) =>
                                setData((prev) => ({
                                  ...prev,
                                  checkList: value,
                                  checkListId: value?.id ?? null,
                                }))
                              }
                              getOptionLabel={getCheckListLabel}
                              isOptionEqualToValue={(option, value) => option?.id === value?.id}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Check list"
                                  placeholder="Optional"
                                  size="small"
                                />
                              )}
                              ListboxProps={{ style: { maxHeight: "220px" } }}
                              size="small"
                            />
                          </div>
                        </>
                      )}

                      <div className="relative w-full">
                        <InputField
                          label="Deadline"
                          name="deadline"
                          type="date"
                          value={data?.deadline?.split("T")[0]}
                          onChange={handleInputChange}
                        />
                      </div>

                      {isEditing && (
                        <div className="relative w-full">
                          <label className="input-label">Status *</label>
                          <FormControl fullWidth>
                            <Autocomplete
                              onChange={(e, value) => {
                                setData((prevState) => ({
                                  ...prevState,
                                  status: value?.value ?? value,
                                }));
                              }}
                              value={
                                statusOptions?.find((option) => option?.value === data?.status) ??
                                null
                              }
                              options={statusOptions || []}
                              getOptionLabel={(option) => option?.label || ""}
                              isOptionEqualToValue={(options, value) =>
                                options.value === value.value
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Status"
                                  type="search"
                                  inputProps={{
                                    ...params.inputProps,
                                    autoComplete: "new-password",
                                  }}
                                />
                              )}
                              ListboxProps={{ style: { maxHeight: "180px" } }}
                              size="small"
                            />
                          </FormControl>
                        </div>
                      )}

                      {summaryItems.length > 0 && (
                        <div className="md:col-span-2 rounded-lg border border-app-border bg-app-surface-muted/50 px-3 py-2">
                          <div className="flex flex-wrap gap-x-5 gap-y-2">
                            {summaryItems.map((item) => (
                              <div key={item.label} className="min-w-0">
                                <p className="text-[10px] uppercase tracking-wide text-app-text-muted m-0">
                                  {item.label}
                                </p>
                                <p
                                  className="text-xs font-medium text-app-text m-0 truncate max-w-[220px]"
                                  title={item.value}
                                >
                                  {item.value}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="relative w-full md:col-span-2">
                        <label className="input-label">Description</label>
                        {/* Mentioning here would notify someone without putting them on the
                            task, so mentions live in the task's discussion instead. */}
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          size="small"
                          value={data?.comment || ""}
                          onChange={(e) =>
                            setData((prev) => ({ ...prev, comment: e.target.value }))
                          }
                          placeholder="Optional context… mention people in the discussion instead"
                        />
                      </div>
                    </div>
                  </FlowCard>

                  <FlowCard
                    cardRef={peopleRef}
                    step={2}
                    title="Assign"
                    hint="Who owns this task? Collaborators are optional."
                    locked={!assignUnlocked}
                    lockHint="Finish essentials above to unlock assigning"
                    complete={isPeopleComplete}
                    active={currentStep === 2}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="relative w-full">
                        <label className="input-label">Assign to *</label>
                        <Autocomplete
                          onChange={(e, value) => {
                            requestPeopleChange(
                              () =>
                                setData((prevState) => ({
                                  ...prevState,
                                  assignTo: value,
                                  assignedToId: value?.id,
                                })),
                              new Set(
                                [
                                  currentUser?.id,
                                  value?.id,
                                  ...additionalAssignees.map((u) => u.id),
                                ].filter((id) => id != null)
                              )
                            );
                          }}
                          required
                          value={
                            isEditing
                              ? orgUsers?.find(
                                  (user) => user?.id === (data?.assignedToId ?? data?.assignTo)
                                ) ?? null
                              : data?.assignTo ?? null
                          }
                          options={cancelSelectedAssignees}
                          getOptionLabel={(option) => option?.name || ""}
                          renderOption={renderAssigneeOption}
                          isOptionEqualToValue={(options, value) => options?.name === value?.name}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Primary assignee"
                              type="search"
                              inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
                              error={attemptedSubmit && !primaryAssigneeId}
                              helperText={
                                attemptedSubmit && !primaryAssigneeId
                                  ? "Choose who this task should go to"
                                  : undefined
                              }
                            />
                          )}
                          ListboxProps={{ style: { maxHeight: "180px" } }}
                          size="small"
                          disabled={!assignUnlocked}
                        />
                      </div>
                      <div className="relative w-full">
                        <label className="input-label">Collaborators</label>
                        <Autocomplete
                          multiple
                          value={additionalAssignees}
                          onChange={(e, value) =>
                            requestPeopleChange(
                              () => setAdditionalAssignees(value),
                              new Set(
                                [
                                  currentUser?.id,
                                  primaryAssignee?.id,
                                  ...value.map((u) => u.id),
                                ].filter((id) => id != null)
                              )
                            )
                          }
                          options={additionalOptions}
                          getOptionLabel={(option) => option?.name || ""}
                          renderOption={renderAssigneeOption}
                          isOptionEqualToValue={(options, value) => options?.id === value?.id}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip label={option?.name} size="small" {...getTagProps({ index })} />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Add collaborators" size="small" />
                          )}
                          size="small"
                          disabled={!assignUnlocked}
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={notifyAdmin && canNotifyAdmin}
                            disabled={!canNotifyAdmin || !assignUnlocked}
                            onChange={(e) => {
                              setNotifyAdmin(e.target.checked);
                              if (e.target.checked) setShowNotifyAdminDialog(true);
                            }}
                          />
                        }
                        label="Notify admin about this task"
                      />
                      <p className="text-xs text-app-text-muted m-0">
                        {canNotifyAdmin
                          ? `Notifies ${adminRecipients.map((user) => user.name).join(", ")}.`
                          : "Nobody on this task is an admin, so there is nobody to notify."}
                      </p>
                      {notifyAdmin && canNotifyAdmin && (
                        <div className="mt-2">
                          {notifyAdminMessage ? (
                            <div className="text-sm bg-app-surface-muted rounded-lg p-3 flex items-start justify-between gap-3">
                              <span className="text-app-text">{notifyAdminMessage}</span>
                              <Button size="small" onClick={() => setShowNotifyAdminDialog(true)}>
                                Edit
                              </Button>
                            </div>
                          ) : (
                            <Button size="small" onClick={() => setShowNotifyAdminDialog(true)}>
                              Write message to admin
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </FlowCard>

                  <FlowCard
                    step={3}
                    title="Subtasks & attachments"
                    hint="Optional — add them now or any time later."
                    locked={!extrasUnlocked}
                    lockHint="Assign someone first to unlock this"
                    complete={extrasUnlocked && (subtasksCount > 0 || attachmentsCount > 0)}
                    active={currentStep === 3}
                  >
                    <div className="space-y-3">
                      <Accordion
                        disableGutters
                        elevation={0}
                        expanded={optionalExpanded.subtasks}
                        onChange={() => toggleOptional("subtasks")}
                        sx={{
                          border: "1px solid var(--app-border, #e2e8f0)",
                          borderRadius: "8px !important",
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography className="!text-sm !font-medium">
                            Subtasks
                            {subtasksCount > 0 && (
                              <span className="text-app-text-muted font-normal">
                                {" "}
                                · {subtasksCount}
                              </span>
                            )}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <SubtaskManager
                            mode="draft"
                            subtasks={draftSubtasks}
                            onDraftChange={setDraftSubtasks}
                            users={subtaskAssignableUsers}
                            taskCreatedDate={data?.createdDate || data?.createdAt || null}
                          />
                        </AccordionDetails>
                      </Accordion>

                      <Accordion
                        disableGutters
                        elevation={0}
                        expanded={optionalExpanded.attachments}
                        onChange={() => toggleOptional("attachments")}
                        sx={{
                          border: "1px solid var(--app-border, #e2e8f0)",
                          borderRadius: "8px !important",
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography className="!text-sm !font-medium">
                            Attachments
                            {attachmentsCount > 0 && (
                              <span className="text-app-text-muted font-normal">
                                {" "}
                                · {attachmentsCount}
                              </span>
                            )}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <UploadFile
                            {...{
                              data,
                              setData,
                              label: "Attachments",
                              imageKey: "attachments",
                              type: "multiple",
                            }}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  </FlowCard>
                </div>

                {/* Progress guide */}
                <aside className="lg:col-span-4 min-w-0 lg:sticky lg:top-4 lg:self-start">
                  <div className="rounded-xl border border-app-border bg-app-surface-alt p-4">
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-app-text-muted m-0 mb-3">
                      Progress
                    </p>
                    <ol className="list-none m-0 p-0">
                      <ProgressStep
                        step={1}
                        label="Essentials"
                        done={isDetailsComplete}
                        active={currentStep === 1}
                        detail={
                          isDetailsComplete
                            ? "Ready"
                            : missingEssentials.length
                            ? `Still need: ${missingEssentials.join(", ")}`
                            : "Client, title, module, priority"
                        }
                      />
                      <ProgressStep
                        step={2}
                        label="Assign owner"
                        done={isPeopleComplete}
                        active={currentStep === 2}
                        locked={!assignUnlocked}
                        detail={
                          !assignUnlocked
                            ? "Locked until essentials are done"
                            : isPeopleComplete
                            ? primaryAssignee?.name || "Assigned"
                            : "Pick who owns this task"
                        }
                      />
                      <ProgressStep
                        step={3}
                        label="Subtasks & attachments"
                        done={extrasUnlocked && (subtasksCount > 0 || attachmentsCount > 0)}
                        active={currentStep === 3}
                        locked={!extrasUnlocked}
                        detail={
                          !extrasUnlocked
                            ? "Locked until someone is assigned"
                            : [
                                subtasksCount ? `${subtasksCount} subtasks` : null,
                                attachmentsCount ? `${attachmentsCount} files` : null,
                              ]
                                .filter(Boolean)
                                .join(" · ") || "Optional — skip if not needed"
                        }
                      />
                    </ol>

                    <div className="mt-2 pt-3 border-t border-app-border space-y-2">
                      <p className="text-xs text-app-text-muted m-0">
                        {currentStep === 1 && "Start with the basics on the left."}
                        {currentStep === 2 && "Great — now choose who this task goes to."}
                        {currentStep === 3 &&
                          "You’re ready to save. Subtasks and files are optional."}
                      </p>
                      <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={isCreatingTask || isUpdatingTask}
                      >
                        {isCreatingTask || isUpdatingTask
                          ? "Saving..."
                          : data?.id
                          ? "Update Task"
                          : "Create Task"}
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          modal || isEditing ? handleClose() : navigate(-1);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </form>
        </div>
      </div>
      {openAlertModal?.state && (
        <DuplicateDataAlertModal
          open={openAlertModal?.state}
          onClose={() => setOpenAlertModal({ state: false, message: "" })}
          message={openAlertModal?.message}
          data={tasksList?.filter(
            (task) =>
              task?.clientId === openAlertModal?.data &&
              task?.status !== "COMPLETED" &&
              task?.status !== "CANCELLED"
          )}
          onSuccess={() => {
            setData((prevState) => ({
              ...prevState,
              clientId: duplicateAlertClient ?? null,
            }));
            setOpenAlertModal({ state: false, message: "" });
          }}
        />
      )}
      <ConfirmDialog
        open={pendingPeopleChange != null}
        title="Unassign their subtasks?"
        message={`${orphanedSubtaskCount} subtask${orphanedSubtaskCount === 1 ? "" : "s"} ${
          orphanedSubtaskCount === 1 ? "is" : "are"
        } assigned to someone who is being removed from this task. Continuing leaves ${
          orphanedSubtaskCount === 1 ? "it" : "them"
        } unassigned — you can pick a new owner afterwards.`}
        confirmLabel="Continue"
        confirmColor="warning"
        onConfirm={confirmPeopleChange}
        onCancel={() => setPendingPeopleChange(null)}
      />

      <NotifyAdminDialog
        open={showNotifyAdminDialog}
        onClose={() => setShowNotifyAdminDialog(false)}
        onSubmit={(message) => {
          setNotifyAdminMessage(message);
          setShowNotifyAdminDialog(false);
        }}
        defaultMessage={notifyAdminMessage}
      />
    </div>
  );
};

export default AddTasks;
